import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Confetti, Mascot, MasteryBar } from '@yuan-math/ui';
import { LessonBlock } from '../components/LessonBlocks.tsx';
import { registry, useLearner } from '../state/LearnerContext.tsx';
import { rewardsFor, useRewards } from '../state/RewardsContext.tsx';
import { NotFoundPage } from './NotFoundPage.tsx';

/**
 * A lesson.
 *
 * Sections are presented one at a time. That is a teaching decision, not a
 * layout one: a wall of eight sections invites scrolling to the practice, while
 * one section at a time keeps the intuition in front of the student before the
 * procedure arrives.
 */
export function LessonPage(): JSX.Element {
  const { lessonId } = useParams();
  const { profile, signedIn, finishLesson, masteryOf } = useLearner();
  const { celebrate } = useRewards();
  const [sectionIndex, setSectionIndex] = useState(0);
  /** Set by the button, never derived from `completed` — see the band below. */
  const [justFinished, setJustFinished] = useState(false);

  const lesson = lessonId ? registry.lesson(lessonId) : undefined;

  useEffect(() => {
    setSectionIndex(0);
    setJustFinished(false);
  }, [lessonId]);

  const neighbours = useMemo(() => (lessonId ? registry.neighbouringLessons(lessonId) : {}), [lessonId]);

  if (!lesson) return <NotFoundPage />;

  const concept = registry.concept(lesson.conceptId);
  const course = registry.courseOfLesson(lesson.id);
  const section = lesson.sections[sectionIndex];
  const isLast = sectionIndex === lesson.sections.length - 1;
  const completed = profile?.completedLessonIds.includes(lesson.id) ?? false;
  const mastery = masteryOf(lesson.conceptId);

  return (
    <article className="page page--reading">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/courses">Courses</Link>
        {course ? (
          <>
            <span aria-hidden="true">/</span>
            <Link to={`/courses/${course.id}`}>{course.title}</Link>
          </>
        ) : null}
      </nav>

      <header className="lesson-header">
        <div>
          <p className="lesson-header__eyebrow">
            {concept ? <Link to={`/concepts/${concept.id}`}>{concept.title}</Link> : null}
            <span> · {lesson.estimatedMinutes} min</span>
          </p>
          <h1 className="page__title">{lesson.title}</h1>
          <p className="page__lead">{lesson.summary}</p>
        </div>
        {signedIn ? <MasteryBar value={mastery.overall} label="Your mastery" size="sm" /> : null}
      </header>

      <section className="objectives">
        <h2 className="objectives__title">By the end of this lesson you will be able to</h2>
        <ul>
          {lesson.objectives.map((objective) => (
            <li key={objective}>{objective}</li>
          ))}
        </ul>
      </section>

      {/* How far through, before how it is divided up. A bar that is already
          a third full is what makes an eight-section lesson feel finishable. */}
      <div className="lesson-progress">
        <div
          className="lesson-progress__track"
          role="progressbar"
          aria-valuenow={sectionIndex + 1}
          aria-valuemin={1}
          aria-valuemax={lesson.sections.length}
          aria-label={`Section ${sectionIndex + 1} of ${lesson.sections.length}`}
        >
          <div
            className="lesson-progress__fill"
            style={{ width: `${((sectionIndex + 1) / lesson.sections.length) * 100}%` }}
          />
        </div>
        <span className="lesson-progress__count">
          {sectionIndex + 1}/{lesson.sections.length}
        </span>
      </div>

      <ol className="section-rail" aria-label="Lesson sections">
        {lesson.sections.map((entry, index) => (
          <li key={entry.kind}>
            <button
              type="button"
              className={`section-rail__item ${index === sectionIndex ? 'section-rail__item--current' : ''} ${index < sectionIndex ? 'section-rail__item--done' : ''}`}
              onClick={() => setSectionIndex(index)}
              aria-current={index === sectionIndex ? 'step' : undefined}
            >
              <span className="section-rail__index">{index + 1}</span>
              <span className="section-rail__label">{entry.title}</span>
            </button>
          </li>
        ))}
      </ol>

      {section ? (
        <section className="lesson-section" key={section.kind}>
          <h2 className="lesson-section__title">{section.title}</h2>
          <div className="lesson-section__body">
            {section.blocks.map((block, index) => (
              <LessonBlock key={index} block={block} />
            ))}
          </div>
        </section>
      ) : null}

      <nav className="lesson-nav" aria-label="Lesson navigation">
        <Button
          variant="secondary"
          onClick={() => setSectionIndex((index) => Math.max(0, index - 1))}
          disabled={sectionIndex === 0}
        >
          Back
        </Button>

        {isLast ? (
          <div className="lesson-nav__end">
            {signedIn ? (
              <Button
                onClick={() => {
                  const feedback = finishLesson({ lessonId: lesson.id, conceptId: lesson.conceptId });
                  celebrate(rewardsFor(feedback, Date.now()));
                  setJustFinished(true);
                }}
                disabled={completed}
                size="lg"
              >
                {completed ? 'Lesson complete' : 'Mark as complete'}
              </Button>
            ) : (
              <Link to="/start" className="btn btn--primary btn--lg">
                Sign in to track this
              </Link>
            )}
            <Link to={`/practice/${lesson.conceptId}`} className="btn btn--secondary btn--lg">
              Practise this concept
            </Link>
          </div>
        ) : (
          <Button onClick={() => setSectionIndex((index) => Math.min(lesson.sections.length - 1, index + 1))} size="lg">
            Continue
          </Button>
        )}
      </nav>

      {/* The celebration is for the transition, not for the state: it shows
          once the lesson has just been finished, and a student re-reading a
          lesson they did last week is not congratulated again. */}
      {justFinished ? (
        <section className="lesson-done">
          <Mascot mood="celebrate" size={96} />
          <h2 className="lesson-done__title">Lesson complete</h2>
          <p className="lesson-done__note">
            Reading it is the first half. Mastery is measured on questions, so a short set now is worth more than
            another read tomorrow.
          </p>
          <Link to={`/practice/${lesson.conceptId}`} className="btn btn--primary btn--lg">
            Practise this concept
          </Link>
          <Confetti />
        </section>
      ) : null}

      {isLast ? (
        <footer className="lesson-footer">
          {neighbours.previous ? (
            <Link to={`/lessons/${neighbours.previous.id}`} className="lesson-footer__link">
              <span>Previous</span>
              <strong>{neighbours.previous.title}</strong>
            </Link>
          ) : (
            <span />
          )}
          {neighbours.next ? (
            <Link to={`/lessons/${neighbours.next.id}`} className="lesson-footer__link lesson-footer__link--next">
              <span>Next lesson</span>
              <strong>{neighbours.next.title}</strong>
            </Link>
          ) : null}
        </footer>
      ) : null}
    </article>
  );
}
